import { useEffect, useState } from "react";
import Card from "../../../components/Card/Card";
import List from "../../../components/List/List";
import Pagination from "../../../components/Pagination/Pagination";
import SyncIcon from "../../../components/SyncIcon/SyncIcon";
import { useSSEContext } from "../../../contexts/sse";
import { useUserStore } from "../../../contexts/userStore";
import UnitService from "../../../services/UnitService";
import "./ListUnits.css";
import { useAuth } from "../../../contexts/auth";
import { toCamelCase } from "../../../utils/string";
import { ParkingLotControlTypeText } from "../../../enums/parkingLotTypes";
import { Tag } from "../../../components/Tag/Tag";
import { useListManager } from "../../../contexts/list.manager";
import { FaCircleCheck } from "react-icons/fa6";

function parseLicenseStatus(data, old) {
	if (!data?.content?.result?.status) {
		return old;
	}
	if (data.content.result.status === 'success') {
		return 'synced';
	}
	return 'notSynced';
}

const ListUnits = ({ showSelectedIcon = false, showTags = false, scopeTags = [], ...props }) => {
	const [units, setUnits] = useState([]);
	const [maxPage, setMaxPage] = useState(1);
	const [currentPage, setCurrentPage] = useState(1);
	const { user } = useAuth();
	const { selectedCondominio, selectedLicenceId } = useUserStore();
	const { sseReceived } = useSSEContext();
	const [collumnClicked, setCollumnClicked] = useState({
		collumnTitle: "createdAt",
		orderDirection: "DESC",
	});
	const { lineSelected } = useListManager();
	const [isLoading, setIsLoading] = useState(true);

	const getUnits = async (
		page,
		customInput1,
		organizationId,
		condominiumId,
		collumnClicked
	) => {
		setIsLoading(true);
		if (condominiumId === 0 || !organizationId) {
			setIsLoading(false);
			return;
		}
		const response = await UnitService.listUnitsPaginated(
			page,
			customInput1,
			organizationId,
			condominiumId,
			collumnClicked,
			true,
			props.tag?.externalId,
			props.linkableToUser ?? null,
			scopeTags ?? [],
		);
		setUnits(response.data.items);
		setMaxPage(response.data.pagination.totalPages);
		setIsLoading(false);
	};

	useEffect(() => {
		getUnits(
			currentPage,
			props.filters,
			user.userData.selectedOrganizationId,
			props.unitId ?? selectedCondominio,
			collumnClicked
		);
	}, [
		selectedCondominio,
		props.unitId,
		user,
		props.filters,
		props.unitsConfigs,
		collumnClicked,
		props.refreshKey,
		props.tag,
		currentPage
	]);

	useEffect(() => {
		if (sseReceived?.action === "replyUnitReceived") {
			if (units.find(u => u.mqtt_id === sseReceived.content?.mqtt_id)) {
				setUnits(prev => prev.map(item => {
					return {
						...item,
						status: parseLicenseStatus(sseReceived, item.status),
					}
				}));
			} else {
				getUnits(
					currentPage,
					props.filters,
					user.userData.selectedOrganizationId,
					props.unitId ?? selectedCondominio,
					collumnClicked
				);
			}
		}
	}, [sseReceived, selectedCondominio, props.unitId, selectedLicenceId, collumnClicked]);

	function mountDynamicColumns() {
		const columns = [];

		if (props.tag?.parkingLimit) {
			columns.push(...[
				{
					collumnLabel: "Total de Vagas",
					type: "text",
					custom: ({ row }) => {
						return row.metadata?.find(item => item.name === 'parkingLotQuantity')?.value || "N/A"
					}
				},
				{
					collumnLabel: "Vagas Ocupadas",
					type: "text",
					custom: ({ row }) => {
						return row.metadata?.find(item => item.name === 'parkingLotBusy')?.value || "N/A"
					}
				},
			]);
		}

		if (props.tag?.parkingControl) {
			columns.push(...[
				{
					collumnLabel: "Máxima Ocupação",
					type: "text",
					custom: ({ row }) => {
						return row.metadata?.find(item => item.name === 'parkingLotSize')?.value || "N/A"
					}
				},
				{
					collumnLabel: "Tipo de Controle",
					type: "text",
					custom: ({ row }) => {
						const parkingControlType = row.metadata?.find(item => item.name === 'parkingControlType')?.value;
						return ParkingLotControlTypeText[parkingControlType] || "N/A"
					}
				},
			]);
		}

		if (props.tag?.areaControl) {
			columns.push(...[
				{
					collumnLabel: "Créditos Habilitados",
					type: "text",
					custom: ({ row }) => {
						return row.metadata?.find(item => item.name === 'creditsEnabled')?.value ? "Sim" : "Não"
					}
				},
				{
					collumnLabel: "Valor de Créditos",
					type: "text",
					custom: ({ row }) => {
						return row.metadata?.find(item => item.name === 'creditsValue')?.value || "N/A"
					}
				},
			]);
		}

		// TODO: Formatar de acordo com o tipo do campo (ex: data, número, booleano, etc)
		if (props.tag?.schema && props.tag.schema.length > 0) {
			props.tag.schema.forEach(field => {
				columns.push({
					collumnLabel: field.name,
					type: "text",
					custom: ({ row }) => {
						return row.metadata?.find(item => item.name === toCamelCase(field.name))?.value || "N/A"
					}
				});
			});
		}

		return columns?.slice(0, 5);
	}

	const additonalColumns = showTags ? [
		{
			collumnLabel: "Etiquetas",
			index: "tags",
			type: "text",
			custom: ({ row }) => (
				<>
					{row.tags && row.tags.length > 0 ? (
						<div style={{ display: 'flex', gap: '4px' }}>
							{row.tags?.map(tag => <Tag key={tag.id} tag={tag} />)}
						</div>
					) : (
						<span style={{ opacity: 0.4 }}>Nenhuma</span>
					)}
				</>
			),
		},
	] : [];

	const preAdditionalColumns = showSelectedIcon ? [
		{
			collumnLabel: "",
			type: "custom",
			custom: ({ row }) => (
				<div className="relative size-[18px]!">
					<input
						type="radio"
						name={`unit-${row.id}`}
						value={row.id}
						checked={lineSelected?.id === row.id}
						className="opacity-0 peer/unit"
					/>
					<span className="absolute size-[calc(100%+6px)] peer-focus/unit:outline peer-focus/unit:outline-(--primaryColor) rounded-full peer-focus/unit:outline-2 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"></span>
					{lineSelected?.id === row.id ? (
						<FaCircleCheck
							size={18}
							className="text-green-500 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
						/>
					) : (
						<div className="size-full bg-transparent border! border-(--borderColor)! rounded-full inset-0 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"></div>
					)}
				</div>
			),
			customHeader: () => (<></>)
		}
	] : [];

	return props.smallList ? (
		<List
			isLoading={isLoading}
			handleCollumnTitleClick={(collumnIndex) =>
				setCollumnClicked(collumnIndex)
			}
			listEntityName={"units"}
			collumsObjIndex={[
				...preAdditionalColumns,
				{
					collumnLabel: "Nome",
					index: "name",
					type: "text",
				},
				...additonalColumns,
			]}
			data={units?.slice(0, 5)}
		/>
	) : (
		<>
			<div className="list-container list-visibility">
				<List
					isLoading={isLoading}
					handleCollumnTitleClick={(collumnIndex) =>
						setCollumnClicked(collumnIndex)
					}
					listEntityName={"units"}
					collumsObjIndex={[
						{
							collumnLabel: "Nome",
							index: "name",
							type: "text",
						},
						...additonalColumns,
						...mountDynamicColumns(),
						{ collumnLabel: "Status", index: "syncStatus", type: "sync-status" },
					]}
					data={units}
				/>
			</div>
			<div className="card-visibility">
				{units &&
					units.map((unit, index) => (
						<Card
							data={unit}
							key={index}
							delete={() => UnitService.deleteUnit(unit.id)}
							navigateOnDelete={"../consulta-units"}
							navigateOnEdit={"../cadastro-units"}
							aditionalButton1={
								<SyncIcon
									isCard={true}
									syncStatus={unit.status}
									data={unit}
									listEntityName={"units"}
								/>
							}
						>
							<p className="card-content-title">Unidade: {unit.customInput1}</p>
							<p className="card-content">
								Quantidade de vagas: {unit.parkingLotQuantity}
							</p>
							<p className="card-content">
								Vagas Ocupadas: {unit.parkingLotBusy}
							</p>
						</Card>
					))}
			</div>
			<Pagination
				rowsPerPage={8}
				maxPage={maxPage}
				changePage={(page) =>
					setCurrentPage(page)
				}
			/>
		</>
	);
};

export default ListUnits;
